// Client-side SEO analyzer: length, keyword presence, Flesch (ES-adapted for Italian).
// Returns { score: 0-100, level: 'good'|'ok'|'weak', hints: [...], detail }
export const analyzeSeo = (text = '', keyword = '', type = 'body') => {
  const t = (text || '').trim();
  if (!t) {
    return { score: 0, level: 'weak', hints: [{ level: 'weak', msg: 'Contenuto vuoto' }], detail: { chars: 0, words: 0, sentences: 0, flesch: 0, kwCount: 0, kwDensity: 0 } };
  }
  const words = t.split(/\s+/).filter(Boolean);
  const wordCount = words.length;
  const chars = t.length;
  const sentences = Math.max(1, (t.match(/[.!?]+(?:\s|$)/g) || []).length);
  const syllables = words.reduce((s, w) => s + countSyllables(w), 0);
  // Vacca-Franchina adjustment (Italian variant of Flesch): 217 - 1.3*(w/s) - 0.6*(syl*100/w)
  const flesch = wordCount > 0
    ? Math.max(0, Math.min(100, 217 - 1.3 * (wordCount / sentences) - 0.6 * (syllables * 100 / wordCount)))
    : 0;

  const kw = (keyword || '').trim().toLowerCase();
  const hasKw = kw && t.toLowerCase().includes(kw);
  const kwCount = kw ? (t.toLowerCase().match(new RegExp(escapeReg(kw), 'g')) || []).length : 0;
  const kwDensity = wordCount > 0 && kw ? kwCount * kw.split(/\s+/).length / wordCount : 0;

  // Length rules per type
  const lengthRule = {
    title: [50, 60],           // meta title
    description: [140, 160],   // meta description
    heading: [20, 80],
    body: [80, 1200],
  }[type] || [50, 600];

  const hints = [];
  let score = 0;

  // Length (30 pt)
  if (chars < lengthRule[0]) {
    hints.push({ level: 'weak', msg: `Troppo corto (${chars} car., ideale ${lengthRule[0]}-${lengthRule[1]})` });
    score += Math.round(30 * chars / lengthRule[0]);
  } else if (chars > lengthRule[1] * 1.4) {
    hints.push({ level: 'weak', msg: `Troppo lungo (${chars} car., ideale ${lengthRule[0]}-${lengthRule[1]})` });
    score += 15;
  } else if (chars > lengthRule[1]) {
    hints.push({ level: 'ok', msg: `Un po' lungo (${chars} car., ideale ${lengthRule[0]}-${lengthRule[1]})` });
    score += 22;
  } else {
    hints.push({ level: 'good', msg: `Lunghezza ottimale (${chars} car.)` });
    score += 30;
  }

  // Keyword (35 pt)
  if (kw) {
    if (!hasKw) {
      hints.push({ level: 'weak', msg: `Keyword "${keyword}" non presente` });
    } else if (kwDensity > 0.05) {
      hints.push({ level: 'ok', msg: `Keyword ripetuta ${kwCount}× (evita keyword stuffing)` });
      score += 20;
    } else {
      hints.push({ level: 'good', msg: `Keyword presente ${kwCount}×` });
      score += 35;
    }
  } else {
    hints.push({ level: 'ok', msg: 'Nessuna focus keyword impostata' });
    score += 15; // partial credit if no keyword expected
  }

  // Readability Flesch/Vacca (35 pt) — thresholds tuned for Italian editorial content
  // (premium/editorial Italian naturally scores 40-60 on Flesch due to compound words + longer sentences).
  const readOk = flesch >= 50;
  if (wordCount < 5) {
    // skip readability check for very short texts (title/heading)
    hints.push({ level: 'ok', msg: 'Testo breve — leggibilità non valutata' });
    score += 25;
  } else if (readOk) {
    hints.push({ level: 'good', msg: `Leggibilità buona (Flesch ${Math.round(flesch)})` });
    score += 35;
  } else if (flesch >= 30) {
    hints.push({ level: 'ok', msg: `Leggibilità media (Flesch ${Math.round(flesch)})` });
    score += 25;
  } else {
    hints.push({ level: 'weak', msg: `Leggibilità difficile (Flesch ${Math.round(flesch)})` });
    score += 15;
  }

  score = Math.max(0, Math.min(100, Math.round(score)));
  const level = score >= 75 ? 'good' : score >= 50 ? 'ok' : 'weak';
  return { score, level, hints, detail: { chars, words: wordCount, sentences, flesch: Math.round(flesch), kwCount, kwDensity } };
};

const escapeReg = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const countSyllables = (w) => {
  const s = (w || '').toLowerCase().replace(/[^a-zàèéìòóù]/g, '');
  if (!s) return 0;
  const groups = s.match(/[aeiouàèéìòóù]+/g);
  return Math.max(1, groups ? groups.length : 1);
};
